import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { ArrowLeft, Mail, Loader2, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

// OPARA v7.0 - Sacred Domain Detection
import { BACKEND_URL } from "@/utils/apiConfig";
import { useTranslation } from "@/hooks/useTranslation";
import { getReadableErrorMessage } from "@/utils/errorHandler";
import { getLoginPath } from "@/utils/localePaths";

function ForgotPasswordPage() {
  const { t, locale } = useTranslation();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email.trim()) {
      toast.error(t('auth.forgot.validation_email_required'));
      return;
    }
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error(t('auth.forgot.validation_email_invalid'));
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/auth/forgot-password`,
        { email: email.trim(), locale },
        {
          headers: {
            "Content-Type": "application/json"
          }
        }
      );

      setEmailSent(true);
      toast.success(t('auth.forgot.success_toast'));

    } catch (error) {
      console.error("Forgot password error:", error);
      toast.error(getReadableErrorMessage(error, t, 'auth.generic_error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Back Button */}
        <Link to={getLoginPath(locale)}>
          <Button
            variant="outline"
            className="mb-6 bg-[#FFDD59] hover:bg-[#E6C84D] text-black font-semibold border-0"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            {t('auth.back_to_login')}
          </Button>
        </Link>

        <Card className="bg-gray-900 border-2 border-[#FFDD59]">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-full bg-gray-800 flex items-center justify-center">
                <Mail className="w-8 h-8 text-[#FFDD59]" />
              </div>
            </div>
            
            <CardTitle className="text-2xl font-bold text-[#FFDD59]">
              {t('auth.forgot.title')}
            </CardTitle>

            <CardDescription className="text-gray-300 mt-2">
              {emailSent
                ? t('auth.forgot.desc_sent')
                : t('auth.forgot.desc_default')
              }
            </CardDescription>
          </CardHeader>

          <CardContent>
            {!emailSent ? (
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Email Input */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    {t('auth.forgot.email_label')}
                  </label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="ornek@email.com"
                    className="bg-gray-800 border-gray-700 text-white"
                  />
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#FFDD59] hover:bg-[#E6C84D] text-black font-bold text-base py-6"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      {t('auth.forgot.sending')}
                    </>
                  ) : (
                    t('auth.forgot.submit_btn')
                  )}
                </Button>
              </form>
            ) : (
              <div className="space-y-4">
                {/* Success Message */}
                <div className="bg-green-900/30 border border-green-700 rounded-lg p-4 flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-gray-300 space-y-2">
                    <p>
                      {t('auth.forgot.sent_body', { email }).split('||').map((seg, i) => (
                        i % 2 === 1 ? <strong key={i}>{seg}</strong> : seg
                      ))}
                    </p>
                    <p className="text-gray-400">
                      {t('auth.forgot.note_validity')}
                    </p>
                    <p className="text-gray-400">
                      {t('auth.forgot.note_spam')}
                    </p>
                    <p className="text-gray-400">
                      {t('auth.forgot.note_check')}
                    </p>
                  </div>
                </div>

                {/* Resend Link */}
                <button
                  onClick={() => setEmailSent(false)}
                  className="w-full text-sm text-[#FFDD59] hover:text-[#E6C84D] transition-colors"
                >
                  {t('auth.forgot.retry')}
                </button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Help Text */}
        <div className="mt-6 text-center text-sm text-gray-400">
          <p>{t('auth.forgot.no_account')}{" "}
            <Link to="/kayit-ol" className="text-[#FFDD59] hover:text-[#E6C84D]">
              {t('auth.forgot.signup_link')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default ForgotPasswordPage;
